import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { HL_FAMILY_KEYS, HL_DOT_HEX } from '@/lib/highlight-utils';
import type { BibleBookmark } from '@/types/bookmark';
import { formatShortDateTime } from '@/lib/prayer-utils';
import type { HighlightFamily } from '@/types/highlight';
import { useNoticeStackClearance } from '@/hooks/useNoticeStackReserve';
import styles from './VersePanelSheet.module.css';

interface VersePanelSheetProps {
  /** The live selection citation ("John 3:16" -> "John 3:16-19" as the user
   *  taps more verses); rendered as the panel header. */
  reference: string;
  readOnlyNotes?: readonly BibleBookmark[];
  /** The family currently marked active (every selected verse shares it), or
   *  null when the selection is mixed / partly-plain. */
  activeFamily: HighlightFamily | null;
  /** True when any selected verse already carries a highlight. */
  canRemove: boolean;
  /** Covering note records on a single-verse selection (0 for a range or none). */
  noteCount: number;
  /** selectedVerses.size > 1 (label pluralization). */
  isRange: boolean;
  /** False during the lossless cutover: hide every core-content mutation. */
  mutationsEnabled: boolean;
  /** Gate the Write-a-journal-entry row (BIBLE_BOOKMARKS_ENABLED). */
  bookmarksEnabled: boolean;
  /** Gate the Pray row (PRAY_MODE_ENABLED). */
  prayEnabled: boolean;
  /** Durable highlight mutation is in flight; mutation controls stay put. */
  busy: boolean;
  /** Localized durable-write failure, cleared by the next attempt. */
  mutationError: string | null;
  /** Keyboard/SR activation moves focus into the dock; pointer taps do not. */
  focusOnOpen: boolean;
  /** Parent ref that follows the verse opener across the panel's alternate
   * selected/normal DOM branches. */
  returnFocusRef: RefObject<HTMLElement | null>;
  onApply: (family: HighlightFamily, sublabel: string | null) => void;
  onRemove: () => void;
  /** BibleChapter: close panel, then (deferred) compose or thread. */
  onWriteNote: () => void;
  /** BibleChapter: close panel, then (deferred) handlePrayOnSelected. */
  onPray: () => void;
  /** BibleChapter: close panel, then handleShareSelected. */
  onShare: () => void;
  /** Done / X / Escape / back: close only (the deferred Write/Pray handoff reads
   *  the surviving selection, so close MUST NOT clear it). */
  onClose: () => void;
  /** Cancel: clear the in-progress selection + anchor, then close (the one exit
   *  that discards the selecting-in-progress state). */
  onCancel: () => void;
  /** Coordinator z for the `verse-panel` NavStack layer (rule #78). */
  zIndex?: number;
}

/**
 * Unified verse panel (Bible reader rethink, verse-panel INTERACTION redesign).
 * Tapping a verse opens ONE DOCKED, NON-MODAL panel (NOT a SheetShell): it
 * reuses the reader's `.prayBar` pattern (`position: fixed`, NO backdrop) so the
 * page stays LIVE and scrollable above it. While the panel is open, tapping more
 * verses on the page grows a CONTIGUOUS passage (BibleChapter owns the selection
 * model + the live `reference`); the panel itself only shows what you can do to
 * the selection: color it with a meaning, write a journal entry, pray it, or
 * share it. A color STACKS in-panel (apply does NOT close: the swatch marks
 * active and "Remove color" appears), so color/recolor/remove then also write,
 * pray, or share the same verse without re-tapping.
 *
 * Layout (device-feedback follow-up): a fixed ~half-height flex column with a
 * PINNED header (live reference + "tap more verses" hint + a close X), an
 * optional PINNED "Saved as" banner (shown when the selection resolves to one
 * saved family: the family dot + name + description, surfacing the already-
 * computed activeFamily prominently and in view), a SCROLLABLE middle (the color
 * grid + Remove + the two-line action rows), and a PINNED footer (Cancel + Done)
 * so the dismiss buttons are always reachable and the overflow is unmistakable. The color grid is the 6 named lead families +
 * "Important / Other" as a single-column labeled list (each with a one-line
 * subtitle); onApply is always called with a null sublabel (the sub-label "More"
 * picker was retired). Below it: a Remove row when the selection already carries
 * a highlight, then the actions block (Write / Pray / Share, each a two-line
 * title + description; Write+Journal-entries gated on bookmarksEnabled, Pray on
 * prayEnabled). Done / X close only; Cancel clears the selection then closes.
 *
 * Because it is now DOCKED reader chrome (not a modal-over-page sheet), the panel
 * uses the `--reader-*` token set (rule #81 amendment for the new container),
 * matching the page it docks to; the family DOTS stay theme-independent fixed
 * hexes (HL_DOT_HEX). It is `role="region"` (NOT a dialog / aria-modal), so the
 * page is interactive; it keeps its own Escape listener and the `verse-panel`
 * NavStack layer (back / swipe / hardware back close it). The two layer-opening
 * actions (Write a journal entry, Pray) use BibleChapter's deferred-open handoff
 * (close panel first, open after the pop lands) so a layer is never pushed in the
 * same tick as this panel's requestClose. No voice, no input fields.
 */
export default function VersePanelSheet({
  reference,
  readOnlyNotes,
  activeFamily,
  canRemove,
  noteCount,
  isRange,
  mutationsEnabled,
  bookmarksEnabled,
  prayEnabled,
  busy,
  mutationError,
  focusOnOpen,
  returnFocusRef,
  onApply,
  onRemove,
  onWriteNote,
  onPray,
  onShare,
  onClose,
  onCancel,
  zIndex,
}: VersePanelSheetProps) {
  const { t, i18n } = useTranslation();
  const [noteLimit, setNoteLimit] = useState(30);
  // App notice stack (v0.2.0 finishing): the stack is fixed above the reader,
  // so while a notice shows the panel's height is capped so its top (the
  // reference and the close X) stays at or below the stack's clearance; the
  // scrollable middle absorbs the difference and the pinned footer stays.
  // BibleChapter's scrollVerseAbovePanel applies the same cap. 0 while no
  // notice shows (the panel is unchanged).
  const noticeClearance = useNoticeStackClearance();
  const panelRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const restoreKeyboardFocus = useCallback(
    (panel: HTMLElement | null, fallbackOpener: HTMLElement | null) => {
      const opener = returnFocusRef.current ?? fallbackOpener;
      if (!opener?.isConnected) return;
      const active = document.activeElement;
      if (
        active === document.body ||
        active === document.documentElement ||
        panel?.contains(active)
      ) {
        opener.focus({ preventScroll: true });
      }
    },
    [returnFocusRef],
  );

  useLayoutEffect(() => {
    if (!focusOnOpen) return;
    const panel = panelRef.current;
    const fallbackOpener =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeRef.current?.focus({ preventScroll: true });
    return () => {
      restoreKeyboardFocus(panel, fallbackOpener);
      // Closing this NavStack-backed dock also reconciles one history entry.
      // Chromium can move focus to <body> after the layout cleanup; restore a
      // second time on the next frame, but never steal focus the user moved.
      window.requestAnimationFrame(() =>
        restoreKeyboardFocus(panel, fallbackOpener),
      );
    };
  }, [focusOnOpen, restoreKeyboardFocus]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, busy]);

  const panelStyle: CSSProperties = {};
  if (zIndex !== undefined) panelStyle.zIndex = zIndex;
  if (noticeClearance > 0) panelStyle.maxHeight = `calc(100dvh - ${noticeClearance}px)`;

  return (
    <section
      ref={panelRef}
      className={styles.panel}
      role="region"
      aria-label={t('bible.versePanelTitle')}
      style={panelStyle}
      tabIndex={-1}
    >
      <div className={styles.header}>
        <span className={styles.headerText}>
          <span className={styles.ref} aria-live="polite">{reference}</span>
          <span className={styles.addHint}>{t('bible.versePanelAddHint')}</span>
        </span>
        <button
          type="button"
          ref={closeRef}
          className={styles.closeBtn}
          aria-label={t('bible.versePanelCloseAria')}
          disabled={busy}
          onClick={onClose}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
            <path
              d="M5 5l10 10M15 5L5 15"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              fill="none"
            />
          </svg>
        </button>
      </div>

      {activeFamily && (
        <div className={styles.currentMark}>
          <span className={styles.currentLabel}>
            {t('bible.versePanelCurrentLabel')}
          </span>
          <span className={styles.currentRow}>
            <span
              className={styles.currentDot}
              style={{ backgroundColor: HL_DOT_HEX[activeFamily] }}
              aria-hidden="true"
            />
            <span className={styles.currentText}>
              <span className={styles.currentName}>
                {t(`bible.hlFamily.${activeFamily}`)}
              </span>
              <span className={styles.currentDesc}>
                {t(`bible.hlFamilyDesc.${activeFamily}`)}
              </span>
              <span className={styles.currentJournal}>
                {t('bible.versePanelCurrentJournal')}
              </span>
            </span>
          </span>
        </div>
      )}

      <div className={styles.scroll}>
        {readOnlyNotes?.slice(0, noteLimit).map((note) => (
          <article key={note.id} className={styles.savedNote}>
            <strong>{note.anchor.reference}</strong>
            <time dateTime={note.createdAt}>{formatShortDateTime(note.createdAt, i18n.language)}</time>
            <p>{note.note || t('bookmark.emptyNotePlaceholder')}</p>
          </article>
        ))}
        {readOnlyNotes && readOnlyNotes.length > noteLimit && (
          <button type="button" className={styles.actionRow} onClick={() => setNoteLimit((limit) => limit + 30)}>
            {t('bookmark.showMore')}
          </button>
        )}
        {busy && (
          <p className={styles.mutationStatus} role="status" aria-live="polite">
            {t('bible.versePanelSaving')}
          </p>
        )}
        {mutationError && (
          <p className={styles.mutationError} role="alert">
            {mutationError}
          </p>
        )}
        {mutationsEnabled && (
          <>
          <p className={styles.journalHint}>{t('bible.versePanelJournalHint')}</p>
          <div className={styles.grid}>
          {HL_FAMILY_KEYS.map((key) => {
            const name = t(`bible.hlFamily.${key}`);
            return (
              <button
                key={key}
                type="button"
                className={styles.swatch}
                aria-pressed={key === activeFamily}
                aria-label={t('bible.highlightSwatchAria', { family: name })}
                disabled={busy}
                onClick={() => onApply(key, null)}
              >
                <span
                  className={styles.dot}
                  style={{ backgroundColor: HL_DOT_HEX[key] }}
                  aria-hidden="true"
                />
                <span className={styles.swatchText}>
                  <span className={styles.swatchName}>{name}</span>
                  <span className={styles.swatchDesc}>
                    {t(`bible.hlFamilyDesc.${key}`)}
                  </span>
                </span>
              </button>
            );
          })}
          </div>

          {canRemove && (
            <button
              type="button"
              className={styles.removeRow}
              disabled={busy}
              onClick={onRemove}
            >
              {t('bible.highlightRemove')}
            </button>
          )}
          </>
        )}

        <div className={styles.actions}>
          {bookmarksEnabled && (
            <button
              type="button"
              className={styles.actionRow}
              onClick={onWriteNote}
            >
              <span className={styles.actionTitle}>
                {noteCount > 0
                  ? t('bible.versePanelNotes', { count: noteCount })
                  : t('bible.versePanelWriteNote')}
              </span>
              <span className={styles.actionDesc}>
                {t('bible.versePanelWriteDesc')}
              </span>
            </button>
          )}
          {prayEnabled && (
            <button type="button" className={styles.actionRow} onClick={onPray}>
              <span className={styles.actionTitle}>
                {isRange
                  ? t('bible.versePanelPrayRange')
                  : t('bible.versePanelPray')}
              </span>
              <span className={styles.actionDesc}>
                {isRange
                  ? t('bible.versePanelPrayRangeDesc')
                  : t('bible.versePanelPrayDesc')}
              </span>
            </button>
          )}
          <button type="button" className={styles.actionRow} onClick={onShare}>
            <span className={styles.actionTitle}>
              {t('bible.versePanelShare')}
            </span>
            <span className={styles.actionDesc}>
              {isRange
                ? t('bible.versePanelShareRangeDesc')
                : t('bible.versePanelShareDesc')}
            </span>
          </button>
        </div>
      </div>

      <div className={styles.footer}>
        <button type="button" className={styles.cancelBtn} disabled={busy} onClick={onCancel}>
          {t('bible.versePanelCancel')}
        </button>
        <button type="button" className={styles.doneBtn} disabled={busy} onClick={onClose}>
          {t('bible.versePanelDone')}
        </button>
      </div>
    </section>
  );
}
